import React, { useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildReflectorColumns,
  Reflector as TableReflector,
} from "./ReflectorData";
import ReleaseLicenseModal, { refusalMessage } from "./ReleaseLicenseModal";
import DeleteReflectorModal, {
  deleteRefusalMessage,
} from "./DeleteReflectorModal";
import ClusterPage from "./Cluster/ClusterPage";
import {
  getReflectorsList,
  releaseReflectorLicense,
  deleteReflectorInstance,
  type ReleaseLicenseCode,
  type DeleteReflectorCode,
} from "./request/request";
import { useAuth0 } from "@auth0/auth0-react";
import { filterReflectors } from "@shared/components/utils/searchUtils";
import { mapAvailabilityToStatus } from "../../helpers/formattersShapedData";
import { useAppSelector } from "@shared/redux/hooks";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { ReflectorManagementOnboardingEmptyState } from "@shared/components/EmptyItems";
import { getTableBodyMaxHeight, REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS } from "../../helpers/tableViewport";
import { useCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";
import { RefreshingIndicator } from "@shared/components/ListLoading";
import { reflectorListCacheKey } from "../../helpers/cacheKeys";

// Using centralized mapAvailabilityToStatus from formattersShapedData

const suggestedKeys = [
  "Reflector ID",
  "Name",
  "Status",
  "Cloud",
  "Region",
  "Cluster",
  "Version",
  "Last Sync",
  "UUID",
];

// Interface for API response
interface Reflector {
  id: number;
  name: string;
  description: string;
  available: number;
  availability: number | null;
  lastUpdate: string;
  licenseId: number;
  license?: { id?: number; serial?: string } | null;
  licenseValid?: boolean;
  licenseReason?: string;
  payg?: boolean;
  version: string;
  data: {
    ha: string;
    cni: Record<string, string>;
    cloud: string;
    ha_ip: string | null;
    region: string;
  };
  tenantId: number;
  subId: string;
  clusterId: number | null;
  localPref: number;
  lastUpdatePeer: string;
  statusPeer: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  cluster: {
    id: number;
    name: string;
    description: string;
    enable: boolean;
  } | null;
  tenant: {
    id: number;
    Name: string;
    nameDisplay: string;
  };
}

/** List row as the table reads it, from the API shape above. */
function shapeReflector(item: Reflector): TableReflector {
  return {
    id: item.id ? String(item.id) : "",
    name: item.name || "",
    subId: item.subId || "",
    status: mapAvailabilityToStatus(item.available),
    cloud: item.data?.cloud ? item.data.cloud.toUpperCase() : "",
    region: item.data?.region || "",
    cluster: item.cluster?.name || (item.clusterId ? String(item.clusterId) : "—"),
    version: item.version || "-",
    lastSync: item.lastUpdate || "-",
    description: item.description || "",
    createdAt: item.createdAt || "",
    // The list endpoint does not send the VPC; the details page has it.
    vpcId: "",
    // Estado de licencia por fila (CTX-355). `hasLicense` mira el id y no
    // el serial: PAYG tambien trae serial -- el del token -- y ese no se
    // libera.
    licenseSerial: item.license?.serial ?? null,
    licenseValid: item.licenseValid,
    licenseReason: item.licenseReason,
    payg: item.payg,
    hasLicense: Boolean(item.license?.id),
  };
}

const NO_REFLECTORS: TableReflector[] = [];
const INITIAL_ROWS_PER_PAGE = 10;

const ReflectorsPage: React.FC = () => {
  const location = useLocation();
  const isDeviceView = location.pathname === "/reflector-v2/management/bgp-reflectors";
  const isClusterView = location.pathname === "/reflector-v2/management/clusters";

	const { tenantId } = useAppSelector(state => state.selection);
  const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;

  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

  // Cached per tenant: coming back to the list (a view, the menu, Back) paints the last
  // answer at once and refreshes it underneath. Only the device view renders this list.
  const listCacheKey =
    isDeviceView && !isAuthLoading && isAuthenticated && tenantId ? reflectorListCacheKey(tenantId) : null;
  const {
    data: fetchedReflectors = NO_REFLECTORS,
    error,
    isLoading: isFetching,
    isRefreshing,
    reload: reloadReflectors,
  } = useCachedFetch(listCacheKey, async () => {
    try {
      const token = await getAccessTokenSilentlyRef.current();
      const response = await getReflectorsList(token, tenantId);
      return (response.data as Reflector[]).map(shapeReflector);
    } catch (err) {
      console.error("Error fetching reflectors:", err);
      throw new Error("Failed to load reflectors. Please try again.");
    }
  });

  // A deleted row leaves the table at once instead of waiting for the refresh that
  // confirms it; the cached list is pruned too, so a revisit cannot paint it back.
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const reflectors = useMemo(
    () => (deletedIds.size ? fetchedReflectors.filter((r) => !deletedIds.has(r.id)) : fetchedReflectors),
    [fetchedReflectors, deletedIds]
  );

  // Liberacion de licencia (CTX-357). El reflector elegido es tambien la señal
  // de que el modal esta abierto: dos estados para una sola cosa se desincronizan.
  const [releaseTarget, setReleaseTarget] = useState<TableReflector | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releaseRefusal, setReleaseRefusal] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TableReflector | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteRefusal, setDeleteRefusal] = useState<string | null>(null);

  const closeReleaseModal = React.useCallback(() => {
    setReleaseTarget(null);
    setReleaseRefusal(null);
  }, []);

  /**
   * El backend decide si la liberacion procede. Un rechazo deja el modal
   * abierto con el motivo -- el del cluster es el que el usuario necesita leer
   * para saber que hacer -- y "ya estaba liberado" llega con 200, asi que se
   * trata como exito y no como error.
   */
  const confirmRelease = React.useCallback(async () => {
    if (!releaseTarget || !tenantId) return;

    setIsReleasing(true);
    setReleaseRefusal(null);
    try {
      const token = await getAccessTokenSilentlyRef.current();
      await releaseReflectorLicense(token, releaseTarget.id, tenantId);
      closeReleaseModal();
      // The row's license state comes from the backend: refetch it (and the cache with it).
      reloadReflectors();
    } catch (err) {
      const code = (
        err as { response?: { data?: { code?: ReleaseLicenseCode } } }
      ).response?.data?.code;
      setReleaseRefusal(refusalMessage(code));
    } finally {
      setIsReleasing(false);
    }
  }, [releaseTarget, tenantId, closeReleaseModal, reloadReflectors]);

  const closeDeleteModal = React.useCallback(() => {
    setDeleteTarget(null);
    setDeleteRefusal(null);
  }, []);

  /**
   * El borrado sigue el mismo trato que la liberacion: el backend decide, y un
   * rechazo deja el modal abierto con el motivo en vez de tirar un error crudo
   * — el del cluster es justamente el que el usuario necesita leer para saber
   * que hacer.
   *
   * Al terminar se recarga el listado: la fila tiene que desaparecer sola, y
   * con ella el cupo de licencia que el endpoint acaba de devolver.
   */
  const confirmDelete = React.useCallback(async () => {
    if (!deleteTarget || !tenantId) return;

    setIsDeleting(true);
    setDeleteRefusal(null);
    try {
      const token = await getAccessTokenSilentlyRef.current();
      await deleteReflectorInstance(token, deleteTarget.id, tenantId);
      closeDeleteModal();
      const deletedId = deleteTarget.id;
      setDeletedIds((prev) => new Set(prev).add(deletedId));
      writeCachedFetch(
        listCacheKey,
        fetchedReflectors.filter((r) => r.id !== deletedId)
      );
      reloadReflectors();
    } catch (err) {
      const code = (
        err as { response?: { data?: { code?: DeleteReflectorCode } } }
      ).response?.data?.code;
      setDeleteRefusal(deleteRefusalMessage(code));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, tenantId, closeDeleteModal, listCacheKey, fetchedReflectors, reloadReflectors]);

  const columns = useMemo(
    () =>
      buildReflectorColumns({
        onReleaseLicense: setReleaseTarget,
        onDeleteReflector: setDeleteTarget,
      }),
    []
  );


  // Filtering logic using utility function
  const filteredData = useMemo(() => {
    return filterReflectors(reflectors, activeFilters);
  }, [activeFilters, reflectors]);

  return (
    <div className="relative">
      <RefreshingIndicator active={isDeviceView && isRefreshing} />
      <GenericCard isCollapsible={false}>
        {/* Device View */}
        {isDeviceView && (
          <>
            <div className="mb-2 p-4">
              <GenericSearchbar
                placeholder="Find by attribute or value (case-sensitive)"
                suggestedKeys={suggestedKeys}
                onFiltersChange={setActiveFilters}
              />
            </div>
            <DataStateView
              loading={isFetching}
              error={error}
              isEmpty={!isFetching && !error && reflectors.length === 0}
              onRetry={reloadReflectors}
              emptyContent={
                <ReflectorManagementOnboardingEmptyState heading="You have no Reflectors yet" />
              }
              loadingContent={
                <TableSkeleton
                  columns={columns.length}
                  rows={8}
                  className="mx-4 mb-4"
                />
              }
            >
              <GenericTable
                columns={columns}
                data={filteredData}
                initialRowsPerPage={INITIAL_ROWS_PER_PAGE}
                rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight={getTableBodyMaxHeight(280)}
              />
            </DataStateView>
          </>
        )}
        {/* Cluster View */}
        {isClusterView && <ClusterPage />}
      </GenericCard>

      <ReleaseLicenseModal
        isOpen={releaseTarget !== null}
        reflectorName={releaseTarget?.name ?? ""}
        licenseSerial={releaseTarget?.licenseSerial ?? undefined}
        isReleasing={isReleasing}
        refusal={releaseRefusal}
        onConfirm={confirmRelease}
        onClose={closeReleaseModal}
      />

      <DeleteReflectorModal
        isOpen={deleteTarget !== null}
        reflectorName={deleteTarget?.name ?? ""}
        isDeleting={isDeleting}
        refusal={deleteRefusal}
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
      />

    </div>
  );
};

export default ReflectorsPage; 